/**
 * Call History Page
 * 
 * Displays SIP call history with detailed lifecycle data including
 * duration, termination reasons, and routing metadata.
 */

import { useState, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, CallHistoryEntry } from '../../services/api';
import { useAutoRefresh, REFRESH_INTERVALS, formatInterval } from '../../hooks/useAutoRefresh';
import {
  Filter,
  Clock,
  X,
  Download,
  Phone,
  PhoneIncoming,
  PhoneOutgoing,
  RefreshCw,
  Settings,
  CheckCircle,
  XCircle,
  AlertTriangle,
} from 'lucide-react';
import {
  EmptyState,
} from '../../components/common';

interface CallFilters {
  status?: 'INVITE' | 'TRYING' | 'RINGING' | 'ANSWERED' | 'TERMINATED' | 'FAILED';
  direction?: 'inbound' | 'outbound';
  transport?: 'UDP' | 'TCP' | 'TLS';
}

export function CallHistoryPage() {
  const [filters, setFilters] = useState<CallFilters>({});
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [selectedCall, setSelectedCall] = useState<CallHistoryEntry | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const queryClient = useQueryClient();

  const activeFilterCount = Object.values(filters).filter(v => v).length;

  // Session-persisted auto-refresh
  const autoRefresh = useAutoRefresh({
    key: 'call_history_autorefresh',
    defaultEnabled: false,
    defaultInterval: 10000,
  });

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (showExportMenu && !target.closest('.export-menu-container')) {
        setShowExportMenu(false);
      }
      if (showSettings && !target.closest('.call-history-settings-container')) {
        setShowSettings(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showExportMenu, showSettings]);

  // Fetch call history
  const { data: callsData, isLoading, isFetching } = useQuery({
    queryKey: ['call-history', { filters, page, pageSize }],
    queryFn: async () => {
      const response = await api.getCallHistory({ 
        limit: pageSize, 
        offset: (page - 1) * pageSize,
        ...filters
      });
      return response;
    },
    refetchInterval: autoRefresh.enabled ? autoRefresh.interval : false,
  });

  const calls: CallHistoryEntry[] = useMemo(() => {
    const raw = (callsData?.data?.calls || []) as CallHistoryEntry[];
    // Show newest calls first (API order is not guaranteed).
    return [...raw].sort((a, b) => {
      const at = Date.parse(a.startTime);
      const bt = Date.parse(b.startTime);
      const aTime = Number.isFinite(at) ? at : 0;
      const bTime = Number.isFinite(bt) ? bt : 0;
      return bTime - aTime;
    });
  }, [callsData?.data?.calls]);
  const summary = callsData?.data?.summary;
  const total = callsData?.data?.total || 0;
  const totalPages = Math.ceil(total / pageSize);

  const handleFilterChange = (key: keyof CallFilters, value: string) => {
    setFilters(prev => {
      const newFilters = { ...prev };
      if (value) {
        (newFilters as Record<string, string>)[key] = value;
      } else {
        delete newFilters[key];
      }
      return newFilters;
    });
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({});
    setPage(1);
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  const formatDuration = (seconds: number | null) => {
    if (seconds === null || seconds === undefined) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins > 0) {
      return `${mins}m ${secs}s`;
    }
    return `${secs}s`;
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'TERMINATED':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'FAILED':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'ANSWERED':
        return <Phone className="h-4 w-4 text-blue-500" />;
      case 'RINGING':
        return <Phone className="h-4 w-4 text-yellow-500 animate-pulse" />;
      case 'INVITE':
      case 'TRYING':
        return <AlertTriangle className="h-4 w-4 text-gray-500" />;
      default:
        return <Phone className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'TERMINATED':
        return 'bg-green-100 text-green-800';
      case 'FAILED':
        return 'bg-red-100 text-red-800';
      case 'ANSWERED':
        return 'bg-blue-100 text-blue-800';
      case 'RINGING':
        return 'bg-yellow-100 text-yellow-800';
      case 'INVITE':
      case 'TRYING':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const downloadCallsAsCSV = () => {
    const headers = ['Start Time', 'End Time', 'Duration (s)', 'Status', 'Direction', 'Transport', 'From', 'To', 'Termination Reason', 'Call ID'];
    const rows = calls.map(call => [
      formatTimestamp(call.startTime),
      call.endTime ? formatTimestamp(call.endTime) : '',
      call.duration || '',
      call.status,
      call.direction,
      call.transport,
      call.fromUri,
      call.toUri,
      call.terminationReason || '',
      call.callId,
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `call-history-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadCallsAsJSON = () => {
    const dataStr = JSON.stringify(calls, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `call-history-${new Date().toISOString().split('T')[0]}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-2xl font-bold text-gray-900">Call History</h1>
            {summary && (
              <>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  {summary.byStatus?.TERMINATED || 0} Completed
                </span>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  {summary.byStatus?.FAILED || 0} Failed
                </span>
                {summary.duration?.avgSeconds > 0 && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    Avg: {formatDuration(summary.duration.avgSeconds)}
                  </span>
                )}
              </>
            )}
          </div>
          <p className="text-gray-600">View SIP call history with duration, routing, and termination details</p>
        </div>
        
        <div className="flex items-center gap-3">
          <button
            onClick={() => queryClient.invalidateQueries({ queryKey: ['call-history'] })}
            disabled={isFetching}
            className="flex items-center gap-2 px-3 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <div className="flex gap-2">
            {/* Settings Button */}
            <div className="relative call-history-settings-container">
              <button
                onClick={() => setShowSettings(!showSettings)}
                className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <Settings className="h-4 w-4 mr-2" />
                Settings
              </button>

              {showSettings && (
                <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 p-4 z-10">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">Auto-refresh</h3>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Refresh interval
                    </label>
                    <select
                      value={autoRefresh.interval}
                      onChange={(e) => autoRefresh.setInterval(parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    >
                      {REFRESH_INTERVALS.map(({ value, label }) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      {autoRefresh.enabled ? 'Refreshing' : 'When enabled, refreshes'} every {formatInterval(autoRefresh.interval)}.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Export Dropdown */}
            <div className="relative export-menu-container">
              <button
                onClick={() => setShowExportMenu(!showExportMenu)}
                className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <Download className="h-4 w-4 mr-2" />
                Export
              </button>

              {showExportMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
                  <button
                    onClick={() => {
                      downloadCallsAsCSV();
                      setShowExportMenu(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Export as CSV
                  </button>
                  <button
                    onClick={() => {
                      downloadCallsAsJSON();
                      setShowExportMenu(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Export as JSON
                  </button>
                </div>
              )}
            </div>

            {/* Auto-refresh Toggle with Settings */}
            <div className="relative">
              <button
                onClick={autoRefresh.toggle}
                className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg border ${
                  autoRefresh.enabled
                    ? 'bg-green-50 text-green-700 border-green-300'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${autoRefresh.enabled ? 'animate-spin' : ''}`} />
                {autoRefresh.enabled ? 'Auto-refresh On' : 'Auto-refresh Off'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg border border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center text-sm font-medium text-gray-700">
            <Filter className="h-4 w-4 mr-2" />
            Filters
            {activeFilterCount > 0 && (
              <span className="ml-2 px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded-full">
                {activeFilterCount}
              </span>
            )}
          </div>
          <button
            onClick={clearFilters}
            disabled={activeFilterCount === 0}
            className="px-3 py-1 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Clear filters
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              value={filters.status || ''}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">All statuses</option>
              <option value="TERMINATED">Terminated (Completed)</option>
              <option value="ANSWERED">Answered (Active)</option>
              <option value="FAILED">Failed</option>
              <option value="RINGING">Ringing</option>
              <option value="TRYING">Trying</option>
              <option value="INVITE">Invite</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Direction
            </label>
            <select
              value={filters.direction || ''}
              onChange={(e) => handleFilterChange('direction', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">All directions</option>
              <option value="inbound">Inbound</option>
              <option value="outbound">Outbound</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Transport
            </label>
            <select
              value={filters.transport || ''}
              onChange={(e) => handleFilterChange('transport', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">All transports</option>
              <option value="TLS">TLS</option>
              <option value="TCP">TCP</option>
              <option value="UDP">UDP</option>
            </select>
          </div>
        </div>
      </div>

      {/* Calls Table */}
      <div className="bg-white rounded-lg border border-gray-200">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading call history...</p>
          </div>
        ) : calls.length === 0 ? (
          <EmptyState
            icon={Phone}
            title="No Calls Yet"
            description="Call history will appear here as calls are processed."
            isFiltered={Object.values(filters).some(v => v)}
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Start Time</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Duration</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Direction</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">From</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">To</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Termination</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Transport</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {calls.map((call) => (
                    <tr
                      key={call.id}
                      onClick={() => setSelectedCall(call)}
                      className="hover:bg-gray-50 cursor-pointer"
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {getStatusIcon(call.status)}
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(call.status)}`}>
                            {call.status}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        <div className="flex items-center">
                          <Clock className="h-4 w-4 mr-2 text-gray-400" />
                          {formatTimestamp(call.startTime)}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                        {formatDuration(call.duration)}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          call.direction === 'inbound' ? 'bg-blue-100 text-blue-800' : 'bg-orange-100 text-orange-800'
                        }`}>
                          {call.direction === 'inbound' ? (
                            <><PhoneIncoming className="h-3 w-3 mr-1" /> In</>
                          ) : (
                            <><PhoneOutgoing className="h-3 w-3 mr-1" /> Out</>
                          )}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 font-mono">
                        <span className="truncate max-w-[150px] block" title={call.fromUri}>
                          {call.fromUri.replace(/^sip:|^sips:/, '').split('@')[0]}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 font-mono">
                        <span className="truncate max-w-[150px] block" title={call.toUri}>
                          {call.toUri.replace(/^sip:|^sips:/, '').split('@')[0]}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {call.terminationReason ? (
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            call.terminationReason.startsWith('200') ? 'bg-green-100 text-green-700' :
                            call.terminationReason.startsWith('4') ? 'bg-yellow-100 text-yellow-700' :
                            call.terminationReason.startsWith('5') || call.terminationReason.startsWith('6') ? 'bg-red-100 text-red-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {call.terminationReason}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          call.transport === 'TLS' ? 'bg-green-100 text-green-800' :
                          call.transport === 'TCP' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {call.transport}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing <span className="font-medium">{(page - 1) * pageSize + 1}</span> to{' '}
                <span className="font-medium">{Math.min(page * pageSize, total)}</span> of{' '}
                <span className="font-medium">{total.toLocaleString()}</span> calls
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Call Detail Modal */}
      {selectedCall && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <h3 className="text-lg font-semibold text-gray-900">Call Details</h3>
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedCall.status)}`}>
                  {selectedCall.status}
                </span>
              </div>
              <button
                onClick={() => setSelectedCall(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <div className="space-y-6">
                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Start Time</label>
                    <p className="text-sm text-gray-900">{formatTimestamp(selectedCall.startTime)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">End Time</label>
                    <p className="text-sm text-gray-900">{selectedCall.endTime ? formatTimestamp(selectedCall.endTime) : '-'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Duration</label>
                    <p className="text-sm text-gray-900 font-medium">{formatDuration(selectedCall.duration)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Termination Reason</label>
                    <p className="text-sm text-gray-900">{selectedCall.terminationReason || '-'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Direction</label>
                    <p className="text-sm text-gray-900 capitalize">{selectedCall.direction}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Transport</label>
                    <p className="text-sm text-gray-900">{selectedCall.transport}</p>
                  </div>
                </div>

                {/* SIP URIs */}
                <div className="border-t pt-4">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">SIP Addresses</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">From</label>
                      <p className="text-sm text-gray-900 font-mono break-all">{selectedCall.fromUri}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">To</label>
                      <p className="text-sm text-gray-900 font-mono break-all">{selectedCall.toUri}</p>
                    </div>
                  </div>
                </div>

                {/* Routing & Metadata */}
                {selectedCall.sipCallData && (
                  <div className="border-t pt-4">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">Routing Information</h4>
                    <div className="grid grid-cols-2 gap-4">
                      {selectedCall.sipCallData.routingRuleName && (
                        <div>
                          <label className="text-sm font-medium text-gray-500">Routing Rule</label>
                          <p className="text-sm text-gray-900">{selectedCall.sipCallData.routingRuleName}</p>
                        </div>
                      )}
                      {selectedCall.sipCallData.sourcePeerName && (
                        <div>
                          <label className="text-sm font-medium text-gray-500">Source Peer</label>
                          <p className="text-sm text-gray-900">
                            {selectedCall.sipCallData.sourcePeerName}
                            {selectedCall.sipCallData.sourcePeerType && (
                              <span className="ml-2 text-xs text-gray-500">({selectedCall.sipCallData.sourcePeerType})</span>
                            )}
                          </p>
                        </div>
                      )}
                      {selectedCall.sipCallData.destinationPeerName && (
                        <div>
                          <label className="text-sm font-medium text-gray-500">Destination Peer</label>
                          <p className="text-sm text-gray-900">
                            {selectedCall.sipCallData.destinationPeerName}
                            {selectedCall.sipCallData.destinationPeerType && (
                              <span className="ml-2 text-xs text-gray-500">({selectedCall.sipCallData.destinationPeerType})</span>
                            )}
                          </p>
                        </div>
                      )}
                      {selectedCall.sipCallData.selectedCodec && (
                        <div>
                          <label className="text-sm font-medium text-gray-500">Selected Codec</label>
                          <p className="text-sm text-gray-900">{selectedCall.sipCallData.selectedCodec}</p>
                        </div>
                      )}
                      {selectedCall.sipCallData.sourceIp && (
                        <div>
                          <label className="text-sm font-medium text-gray-500">Source IP</label>
                          <p className="text-sm text-gray-900 font-mono">
                            {selectedCall.sipCallData.sourceIp}
                            {selectedCall.sipCallData.sourcePort && `:${selectedCall.sipCallData.sourcePort}`}
                          </p>
                        </div>
                      )}
                      {selectedCall.sipCallData.userAgent && (
                        <div className="col-span-2">
                          <label className="text-sm font-medium text-gray-500">User Agent</label>
                          <p className="text-sm text-gray-900 font-mono break-all">{selectedCall.sipCallData.userAgent}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Call ID & Container */}
                <div className="border-t pt-4">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Technical Details</h4>
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Call ID</label>
                      <p className="text-sm text-gray-900 font-mono break-all">{selectedCall.callId}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Container ID</label>
                      <p className="text-sm text-gray-900 font-mono">{selectedCall.containerId}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
